import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ensureUser } from '../../util/data';
import {
  isInquiryProcessAlias,
  isNegotiationProcessAlias,
  OFFER,
} from '../../transactions/transaction';

import { AvatarMedium, IconReviewStar, Modal, NamedLink } from '../../components';
import InquiryForm from './InquiryForm/InquiryForm';

import contactIcon from '../../assets/listing/icon-contact.svg';

import css from './SectionLender.module.css';

const CONTACT_USER_LINK = 'inquiryModalContactUserLink';

/**
 * Average of the public reviews this page already loaded, to one decimal.
 * Returns null when there is nothing to average — the row is then left out
 * rather than filled with a placeholder.
 */
const getReviewSummary = reviews => {
  const ratings = (reviews || [])
    .map(r => r?.attributes?.rating)
    .filter(r => typeof r === 'number');
  if (ratings.length === 0) {
    return null;
  }
  const average = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
  return { average: Math.round(average * 10) / 10, count: ratings.length };
};

/**
 * The lender behind the listing: who they are, how this listing has been
 * reviewed, and the two ways to reach them.
 *
 * The rating line is derived from the listing's own public reviews, which is the
 * only rating this page actually has; with no reviews the line is omitted.
 *
 * @component
 * @param {Object} props
 * @param {string} props.title listing title, passed to the inquiry form
 * @param {Object} props.listing the listing entity
 * @param {string} props.authorDisplayName author's display name
 * @param {Array} props.reviews public reviews of this listing
 * @param {Function} props.onContactUser opens the inquiry modal
 * @param {boolean} props.isInquiryModalOpen
 * @param {Function} props.onCloseInquiryModal
 * @param {Object} props.sendInquiryError
 * @param {boolean} props.sendInquiryInProgress
 * @param {Function} props.onSubmitInquiry
 * @param {Function} props.onManageDisableScrolling
 * @returns {JSX.Element|null}
 */
const SectionLender = props => {
  const intl = useIntl();
  const {
    className,
    title,
    listing,
    isOwnListing,
    authorDisplayName,
    reviews,
    onContactUser,
    isInquiryModalOpen,
    onCloseInquiryModal,
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    onManageDisableScrolling,
  } = props;

  const author = listing?.author;
  if (!author) {
    return null;
  }

  const ensuredAuthor = ensureUser(author);
  const authorId = ensuredAuthor.id?.uuid;
  const displayName = ensuredAuthor.attributes.profile.displayName || authorDisplayName;

  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  const unitType = listing?.attributes?.publicData?.unitType || '';
  const isInquiryProcess = isInquiryProcessAlias(transactionProcessAlias);
  const isNegotiationProcess = isNegotiationProcessAlias(transactionProcessAlias);
  const showContact =
    !isOwnListing && !(isInquiryProcess || (isNegotiationProcess && unitType === OFFER));

  const summary = getReviewSummary(reviews);

  return (
    <section id="author" className={classNames(css.root, className)}>
      {/* The card carries no visible title in the design; the section still needs
          a name for anyone navigating by landmark or heading. */}
      <h2 className={css.visuallyHidden}>
        <FormattedMessage id="ListingPage.aboutProviderTitle" />
      </h2>

      <div className={css.identity}>
        <AvatarMedium className={css.avatar} user={author} disableProfileLink />
        <div className={css.identityText}>
          <p className={css.name}>{displayName}</p>
          {summary ? (
            <p className={css.rating}>
              <span className={css.ratingStars}>
                <IconReviewStar className={css.star} isFilled />
                <span className={css.ratingValue}>
                  {intl.formatNumber(summary.average, { maximumFractionDigits: 1 })}
                </span>
              </span>
              <span className={css.ratingSeparator} aria-hidden="true" />
              <span className={css.ratingCount}>
                <FormattedMessage
                  id="ListingPage.lenderReviewCount"
                  values={{ count: summary.count }}
                />
              </span>
            </p>
          ) : null}
        </div>
      </div>

      <div className={css.actions}>
        {authorId ? (
          <NamedLink className={css.action} name="ProfilePage" params={{ id: authorId }}>
            <FormattedMessage id="UserCard.viewProfileLink" />
          </NamedLink>
        ) : null}
        {showContact ? (
          <button
            type="button"
            id={CONTACT_USER_LINK}
            className={css.action}
            onClick={() => onContactUser(author)}
          >
            <img className={css.contactIcon} src={contactIcon} alt="" width={16} height={16} aria-hidden="true" />
            <FormattedMessage id="UserCard.contactUser" />
          </button>
        ) : null}
      </div>

      <Modal
        id="ListingPage.inquiry"
        contentClassName={css.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={onCloseInquiryModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={CONTACT_USER_LINK}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
        />
      </Modal>
    </section>
  );
};

export default SectionLender;
